import { type ReactNode, useEffect } from "react";
import { XMarkIcon } from "@heroicons/react/24/outline";
import clsx from "clsx";

interface BottomSheetProps {
  show: boolean;
  title?: string;
  children: ReactNode; // content
  footer?: string;
  heightClasses?: string; // Tailwind utility class，用於設定高度
  onClose: () => void;
}

const BottomSheet: React.FC<BottomSheetProps> = ({
  show,
  title = "",
  children,
  footer = "",
  heightClasses = "",
  onClose,
}) => {
  const handleClose = () => {
    onClose();
  };

  const handleBackdropClick = () => {
    onClose();
  };

  // 當底部抽屜打開時，防止背景滾動
  useEffect(() => {
    if (!show) {
      return;
    }

    // 保存原始樣式值
    const originalOverflow = document.body.style.overflow;
    const originalTouchAction = document.body.style.touchAction;
    const originalHeight = document.body.style.height;

    // 防止背景滾動
    document.body.style.overflow = "hidden";
    document.body.style.touchAction = "none";
    document.body.style.height = "100vh";

    // 清理函數：恢復原始樣式
    return () => {
      document.body.style.overflow = originalOverflow;
      document.body.style.touchAction = originalTouchAction;
      document.body.style.height = originalHeight;
    };
  }, [show]);

  // (無法正確顯示在畫面最上層時，再改用 createPortal)
  return (
    <>
      {/* Backdrop */}
      {show && (
        <div
          onClick={handleBackdropClick}
          className="fixed inset-0 z-bottomSheet h-full w-full bg-black/30"
        />
      )}

      {/* BottomSheet */}
      <div
        className={clsx(
          "fixed inset-x-0 bottom-0 z-bottomSheet flex flex-col gap-4 rounded-t-xl bg-white p-4 pb-8 transition-transform duration-300 ease-in-out",
          show ? "translate-y-0" : "translate-y-full",
          heightClasses || "",
          "brand-dvh-screen-minus-header",
        )}
      >
        {/* Header */}
        <div className="flex items-center justify-between">
          <h4 className="min-h-4 flex-1 text-center text-base font-bold text-base-content">
            {title}
          </h4>
          <button onClick={handleClose} className="absolute right-4">
            <XMarkIcon className="h-6 w-6" />
          </button>
        </div>

        {/* Divider */}
        <div className="divider m-0 -mx-4 h-0" />

        {/* Content */}
        <div className="overflow-y-scroll pt-4">{children}</div>

        {/* Footer */}
        <div>{footer}</div>
      </div>
    </>
  );
};

export default BottomSheet;
